/* ==========================================================================
   VITTRIUM DR SECTION LIBRARY V1 — core behaviour
   ==========================================================================

   Three custom elements and nothing else. No global listeners, no DOM scan on
   load, no polling, no libraries.

   Custom elements are used deliberately rather than a DOMContentLoaded sweep:
   the browser upgrades an element the moment it enters the document, so a
   section a merchant drops into the Theme Editor works immediately, with no
   reload and no section:load hook to remember to wire up.

   Everything degrades: with JS disabled the accordion panels are open, the
   carousel is a horizontally scrollable list, and nothing is unreachable.

   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     <vdr-accordion single>
     ------------------------------------------------------------------------
     Expects, per item:
       <button class="vdr-accordion__trigger" aria-expanded="…" aria-controls="ID">
       <div id="ID" class="vdr-accordion__panel" role="region" [hidden]>

     The markup is already correct and accessible when it is rendered by
     Liquid; this class only toggles state. The button element is what makes
     Enter, Space and focus work — not code here.
     ---------------------------------------------------------------------- */

  class VdrAccordion extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this._bound = false;
    }

    get single() {
      return this.hasAttribute('single');
    }

    _onClick(event) {
      const trigger = event.target.closest('.vdr-accordion__trigger');
      if (!trigger || !this.contains(trigger)) return;

      const panel = this.querySelector('#' + CSS.escape(trigger.getAttribute('aria-controls')));
      if (!panel) return;

      const willOpen = trigger.getAttribute('aria-expanded') !== 'true';

      // "Single" closes the others first, so only one panel is ever open.
      if (willOpen && this.single) {
        this.querySelectorAll('.vdr-accordion__trigger[aria-expanded="true"]').forEach((other) => {
          if (other === trigger) return;
          const otherPanel = this.querySelector('#' + CSS.escape(other.getAttribute('aria-controls')));
          other.setAttribute('aria-expanded', 'false');
          if (otherPanel) otherPanel.hidden = true;
        });
      }

      trigger.setAttribute('aria-expanded', String(willOpen));
      panel.hidden = !willOpen;
    }
  }

  /* ------------------------------------------------------------------------
     <vdr-carousel>
     ------------------------------------------------------------------------
     Scrolling is CSS scroll-snap. This only moves the scroll position and
     keeps the two buttons' disabled state honest, because a button that looks
     available and does nothing is worse than no button.
     ---------------------------------------------------------------------- */

  class VdrCarousel extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.track = this.querySelector('.vdr-carousel__track');
      if (!this.track) return;

      this.prev = this.querySelector('[data-vdr-prev]');
      this.next = this.querySelector('[data-vdr-next]');
      this._bound = true;

      this._onPrev = () => this._scrollBy(-1);
      this._onNext = () => this._scrollBy(1);
      this._sync = this._sync.bind(this);

      if (this.prev) this.prev.addEventListener('click', this._onPrev);
      if (this.next) this.next.addEventListener('click', this._onNext);
      this.track.addEventListener('scroll', this._sync, { passive: true });

      // The track's width decides how far a page is, so re-check on resize.
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(this._sync);
        this._ro.observe(this.track);
      }

      this._sync();
    }

    disconnectedCallback() {
      if (this.prev) this.prev.removeEventListener('click', this._onPrev);
      if (this.next) this.next.removeEventListener('click', this._onNext);
      if (this.track) this.track.removeEventListener('scroll', this._sync);
      if (this._ro) this._ro.disconnect();
      this._bound = false;
    }

    _step() {
      const first = this.track.firstElementChild;
      if (!first) return this.track.clientWidth;
      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    }

    _scrollBy(direction) {
      // Right-to-left locales scroll the other way; honour the document.
      const rtl = window.getComputedStyle(this.track).direction === 'rtl';
      const sign = rtl ? -1 : 1;
      this.track.scrollBy({ left: this._step() * direction * sign, behavior: 'smooth' });
    }

    _sync() {
      if (!this.prev && !this.next) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth;
      // 2px of slack: sub-pixel layout means scrollLeft rarely hits max exactly.
      const atStart = Math.abs(scrollLeft) <= 2;
      const atEnd = Math.abs(Math.abs(scrollLeft) - max) <= 2 || max <= 0;
      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
    }
  }

  /* ------------------------------------------------------------------------
     <vdr-tabs>
     ------------------------------------------------------------------------
     Full WAI-ARIA tab semantics including arrow keys, Home and End. Used by
     sections that group content into phases.
     ---------------------------------------------------------------------- */

  class VdrTabs extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!this.tabs.length) return;
      this._bound = true;

      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeydown);
      this._bound = false;
    }

    _select(tab) {
      this.tabs.forEach((other) => {
        const selected = other === tab;
        other.setAttribute('aria-selected', String(selected));
        // Roving tabindex: only the active tab is in the tab order.
        other.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(other.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
    }

    _onClick(event) {
      const tab = event.target.closest('[role="tab"]');
      if (tab && this.contains(tab)) this._select(tab);
    }

    _onKeydown(event) {
      const current = event.target.closest('[role="tab"]');
      if (!current || !this.contains(current)) return;

      const index = this.tabs.indexOf(current);
      let target = null;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        target = this.tabs[(index + 1) % this.tabs.length];
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        target = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
      } else if (event.key === 'Home') {
        target = this.tabs[0];
      } else if (event.key === 'End') {
        target = this.tabs[this.tabs.length - 1];
      }

      if (!target) return;
      event.preventDefault();
      this._select(target);
      target.focus();
    }
  }

  // Guarded so a second copy of this file — a duplicated theme, a merchant who
  // pasted the tag twice — cannot throw and take the page's scripts down.
  if (!window.customElements.get('vdr-accordion')) {
    window.customElements.define('vdr-accordion', VdrAccordion);
  }
  if (!window.customElements.get('vdr-carousel')) {
    window.customElements.define('vdr-carousel', VdrCarousel);
  }
  if (!window.customElements.get('vdr-tabs')) {
    window.customElements.define('vdr-tabs', VdrTabs);
  }
})();
